import { useEffect, useRef } from 'react';

import { useSponsoredAds } from 'hooks/useSponsoredAds';
import { trackOperation } from 'events/ops/opsEvent';

/**
 * Wraps useSponsoredAds to emit SponsoredResultsLoad ops events.
 * Emits exactly once per fetch cycle — success when results arrive, failure on error.
 * Skips tracking entirely when ads are disabled (hook returns undefined).
 */
export function useSponsoredAdsWithOps(...args: Parameters<typeof useSponsoredAds>) {
  const sponsoredAds = useSponsoredAds(...args);
  const trackedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (sponsoredAds === undefined) {
      return;
    }

    const fetchKey = JSON.stringify(args[0]);
    if (trackedKeyRef.current === fetchKey) {
      return;
    }
    trackedKeyRef.current = fetchKey;

    if (sponsoredAds.error) {
      trackOperation('SponsoredResultsLoad', 'failure');
    } else if (sponsoredAds.results) {
      trackOperation('SponsoredResultsLoad', 'success');
    }
  }, [sponsoredAds]);

  return sponsoredAds;
}
